import moment from "@/utils/moment";

import specialDatesHelper from "@/utils/helpers/specialDatesHelper";

// Range where a cleaning can happen.
const INITIAL_BUSINESS_HOUR = 7;
const FINAL_BUSINESS_HOUR = 22;

// Latest hour a cleaning can start.
const FINAL_SCHEDULE_HOUR = 19;

// Time where the same day purchase should be block.
const MAX_SAME_DAY_PURCHASE_TIME = 16;

// Range where a cleaning can only be purchase for the next day after the CRITICAL_TIME.
const STARTS_SLEEPING_TIME = 18;
const ENDS_SLEEPING_TIME = 8;
const CRITICAL_TIME = 14;

// Hours from now that a user is allow to buy a cleaning.
const NEXT_PURCHASABLE_HOUR = 3;

// Range of hours allowed for each cleaning.
const MIN_AMOUNT_OF_HOURS = 1;
const MAX_AMOUNT_OF_HOURS = 8;

function canBuySameDayPurchase(date) {
  return date.hours() < MAX_SAME_DAY_PURCHASE_TIME;
}

function isToday(date) {
  return date.isSame(moment(), "day");
}

function isTomorrow(date) {
  return date.isSame(moment().add(1, "days"), "day");
}

function nextPurchasableHour() {
  const now = moment();

  return now.hours() + NEXT_PURCHASABLE_HOUR + (now.minutes() > 0 ? 1 : 0);
}

function isAfterStartSleepingTime() {
  const now = moment().hours();

  return now >= STARTS_SLEEPING_TIME;
}

function isBeforeEndSleepingTime() {
  const now = moment().hours();

  return now < ENDS_SLEEPING_TIME;
}

function inTimeForSameDayPurchase(finalScheduleHour) {
  return nextPurchasableHour() <= finalScheduleHour;
}

function getAvailableHours(from, to) {
  return [...Array(to + 1).keys()].filter(number => number > from - 1);
}

function notBetweenAllowedScheduleTime(startingAt) {
  return startingAt < INITIAL_BUSINESS_HOUR && startingAt > FINAL_SCHEDULE_HOUR;
}

function hourPart(date) {
  return parseInt(moment(date).hours());
}

const fromDate = () => {
  let startingDate = moment();

  if (!canBuySameDayPurchase(startingDate)) {
    startingDate.add(1, "days");
  }

  return startingDate.startOf("day").toDate();
};

const toDate = () => {
  return moment()
    .add(35, "days")
    .toDate();
};

const nextAvailableDay = () => {
  let nextAvailableDay = moment();

  do {
    nextAvailableDay = nextAvailableDay.add(1, "days");
  } while (specialDatesHelper.isPurchaseDisabled(nextAvailableDay));

  return nextAvailableDay;
};

const nextAvailableHours = (desiredDate = null) => {
  desiredDate = moment(desiredDate);

  const holiday = specialDatesHelper.holiday(desiredDate);

  if (holiday && !holiday.activePurchase) return [];

  const finalScheduleHour =
    holiday && holiday.maxStartsAt ? holiday.maxStartsAt : FINAL_SCHEDULE_HOUR;

  if (isToday(desiredDate)) {
    if (isBeforeEndSleepingTime()) {
      return getAvailableHours(CRITICAL_TIME, finalScheduleHour);
    }

    if (inTimeForSameDayPurchase(finalScheduleHour)) {
      return getAvailableHours(nextPurchasableHour(), finalScheduleHour);
    }

    return [];
  }

  if (isTomorrow(desiredDate) && isAfterStartSleepingTime()) {
    return getAvailableHours(CRITICAL_TIME, finalScheduleHour);
  }

  return getAvailableHours(INITIAL_BUSINESS_HOUR, finalScheduleHour);
};

const purchasableHours = () => {
  return getAvailableHours(INITIAL_BUSINESS_HOUR, FINAL_SCHEDULE_HOUR);
};

const purchasableDurations = () => {
  return getAvailableHours(MIN_AMOUNT_OF_HOURS, MAX_AMOUNT_OF_HOURS);
};

const nextAvailableDurations = (startingAt, endingAt = null) => {
  if (notBetweenAllowedScheduleTime(startingAt)) {
    return [];
  }

  return getAvailableHours(
    1,
    Math.min((endingAt || FINAL_BUSINESS_HOUR) - startingAt, 8)
  );
};

const scheduleEventPeriod = event => {
  if (!event) {
    return;
  }

  const availableHours = nextAvailableHours(event.starts_at);

  if (!availableHours.length) {
    return;
  }

  const from = hourPart(event.starts_at);
  const to = hourPart(event.ends_at);

  const holiday = specialDatesHelper.holiday(moment(event.starts_at));
  const finalScheduleHour =
    holiday && holiday.maxStartsAt ? holiday.maxStartsAt : FINAL_SCHEDULE_HOUR;

  if (to <= availableHours[0] || from > finalScheduleHour) {
    return;
  }

  return [Math.max(availableHours[0], from), to];
};

const orderScheduleEvents = events => {
  return events.sort(
    (a, b) =>
      moment(a["starts_at"]).valueOf() - moment(b["starts_at"]).valueOf()
  );
};

const notBusinessDay = desiredDate => {
  const isHoliday = specialDatesHelper.isHoliday(desiredDate);
  const isBusinessDay = specialDatesHelper.isBusinessDay(desiredDate);

  return isHoliday || !isBusinessDay;
};

export default {
  fromDate,
  toDate,
  nextAvailableDay,
  nextAvailableHours,
  purchasableHours,
  purchasableDurations,
  nextAvailableDurations,
  scheduleEventPeriod,
  orderScheduleEvents,
  notBusinessDay
};



// WEBPACK FOOTER //
// ./src/utils/helpers/orderSchedulerHelper.js